import { z } from 'zod';
import { countryCodeSchema, idSchema, isoDateSchema, isoDateTimeSchema } from './common.js';
import { documentTaxStrategySchema } from './enums.js';
export const taxDeliveryModeSchema = z.enum(['SHIPMENT', 'PICKUP', 'UNKNOWN']);
export const taxSupplyTypeSchema = z.enum(['GOODS', 'SERVICES', 'MIXED', 'UNKNOWN']);
export const taxDeterminationAssessmentSchema = z.enum([
    'ELIGIBLE',
    'NOT_ELIGIBLE',
    'REVIEW_REQUIRED',
]);
export const taxDeterminationReasonSchema = z.enum([
    'CROSS_BORDER_EU_DESTINATION',
    'DOMESTIC_DESTINATION',
    'DESTINATION_NOT_EU',
    'DESTINATION_MISSING',
    'SELLER_NOT_EU',
    'CUSTOMER_IS_COMPANY',
    'CUSTOMER_NOT_COMPANY',
    'CUSTOMER_CONFIGURED_EU_B2B',
    'CUSTOMER_NOT_CONFIGURED_EU_B2B',
    'VAT_ID_VALID_AND_CURRENT',
    'VAT_ID_MISSING',
    'VAT_ID_INVALID',
    'VAT_ID_COUNTRY_MISMATCH',
    'VAT_VALIDATION_MISSING',
    'VAT_VALIDATION_STALE',
    'VAT_VALIDATION_UNAVAILABLE',
    'VAT_ID_CHANGED_SINCE_DOCUMENT_SNAPSHOT',
    'BILLING_AND_DESTINATION_DIFFER',
    'GOODS_SUPPLY',
    'SERVICE_SUPPLY_REVIEW',
    'MIXED_SUPPLY_REVIEW',
    'SUPPLY_TYPE_UNKNOWN',
    'PICKUP_REVIEW',
    'DELIVERY_MODE_UNKNOWN',
    'TRANSPORT_EVIDENCE_AVAILABLE',
    'TRANSPORT_EVIDENCE_REQUIRED',
    'EXISTING_EXPORT_CONFIGURATION',
    'EXISTING_REVERSE_CHARGE_CONFIGURATION',
    'SPECIAL_CASE_REVIEW',
    'MASTER_FACTS_INCOMPLETE',
]);
export const taxDeterminationActionSchema = z.enum([
    'VERIFY_VAT_ID',
    'CONFIRM_DESTINATION',
    'COLLECT_TRANSPORT_EVIDENCE',
    'REVIEW_SUPPLY_TYPE',
    'REVIEW_PICKUP_OR_SPECIAL_CASE',
    'PROVIDE_OVERRIDE_REASON',
    'COMPLETE_MASTER_FACTS',
]);
export const taxDeterminationVatEvidenceSchema = z.object({
    vatId: z.string().nullable(),
    verificationId: idSchema.nullable(),
    status: z
        .enum(['VALID', 'INVALID', 'NOT_CHECKABLE', 'SERVICE_UNAVAILABLE', 'UNCHECKED'])
        .nullable(),
    checkedAt: isoDateTimeSchema.nullable(),
    validUntilRecheckAt: isoDateTimeSchema.nullable(),
    requestIdentifier: z.string().nullable(),
    mappingVersion: z.string().nullable(),
    matchesCurrentVatId: z.boolean(),
});
const completeTaxDeterminationSnapshotSchema = z.object({
    version: z.literal(1),
    evaluatedAt: isoDateTimeSchema,
    relevantDate: isoDateSchema,
    selectedTaxStrategy: documentTaxStrategySchema,
    suggestedTaxStrategy: documentTaxStrategySchema,
    assessment: taxDeterminationAssessmentSchema,
    sellerCountryCode: countryCodeSchema,
    customerCountryCode: countryCodeSchema,
    billingCountryCode: countryCodeSchema,
    destinationCountryCode: countryCodeSchema.nullable(),
    sellerIsEuMember: z.boolean(),
    destinationIsEuMember: z.boolean().nullable(),
    membershipDataVersion: z.string(),
    customerIsBusiness: z.boolean(),
    customerTaxCategory: z.enum([
        'DOMESTIC',
        'EU_B2B',
        'EU_B2C',
        'THIRD_COUNTRY',
        'REVERSE_CHARGE_DOMESTIC',
        'TAX_EXEMPT',
    ]),
    deliveryMode: taxDeliveryModeSchema,
    supplyType: taxSupplyTypeSchema,
    transportEvidence: z.object({
        available: z.boolean(),
        sourceDocumentId: idSchema.nullable(),
        sourceDocumentType: z.string().nullable(),
    }),
    vatEvidence: taxDeterminationVatEvidenceSchema,
    reasons: z.array(taxDeterminationReasonSchema),
    requiredActions: z.array(taxDeterminationActionSchema),
    manualOverride: z
        .object({
        suggestedTaxStrategy: documentTaxStrategySchema,
        selectedTaxStrategy: documentTaxStrategySchema,
        reason: z.string().min(10).max(1_000),
        actorId: idSchema,
        recordedAt: isoDateTimeSchema,
    })
        .nullable(),
});
/** V1 remains strict for existing snapshots; V2 explicitly records incomplete draft facts. */
export const taxDeterminationSnapshotSchema = z.discriminatedUnion('version', [
    completeTaxDeterminationSnapshotSchema,
    completeTaxDeterminationSnapshotSchema.extend({
        version: z.literal(2),
        customerCountryCode: countryCodeSchema.nullable(),
        billingCountryCode: countryCodeSchema.nullable(),
        customerTaxCategory: completeTaxDeterminationSnapshotSchema.shape.customerTaxCategory.nullable(),
        assessment: z.literal('REVIEW_REQUIRED'),
        incompleteFields: z
            .array(z.enum([
            'customer.countryCode',
            'billingAddress.countryCode',
            'shippingAddress.countryCode',
            'customer.taxCategory',
            'items.productType',
        ]))
            .min(1),
    }),
]);
export const taxDeterminationPreviewInputSchema = z.object({
    customerId: idSchema,
    documentDate: isoDateSchema,
    billingCountryCode: countryCodeSchema.nullable(),
    shippingCountryCode: countryCodeSchema.nullable().default(null),
    deliveryMode: taxDeliveryModeSchema.default('UNKNOWN'),
    productIds: z.array(idSchema).max(500).default([]),
    hasManualArticle: z.boolean().default(false),
    selectedTaxStrategy: documentTaxStrategySchema,
    previousCustomerVatId: z.string().nullable().default(null),
});
//# sourceMappingURL=tax-determination.js.map